import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import {
  IMPORT_STATE,
  type TrustpilotBusinessUnit,
  type TrustpilotInfo,
} from '../types';
import TrustpilotSearchModal from './TrustpilotSearchModal';
import TrustpilotTokenModal from './TrustpilotTokenModal';
import { useTrustpilot } from './useTrustpilot';

export const TrustpilotItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    trustpilotWorkspace,
    disconnectTrustpilotMutation,
    requestImportMutation,
    setTrustpilotWorkspace,
    setTrustpilotBusinessUnits,
  } = useTrustpilot(!isConnector);

  const tokenModalState = useModalState();
  const importModalState = useModalState<{ isNew: boolean }>();
  const searchUnitModalState = useModalState();

  const handleConnectComplete = (workspace: TrustpilotInfo) => {
    if (!currWorkspace) return;

    setTrustpilotWorkspace(workspace);
    fetchFeedbackSources(currWorkspace.alias);
    tokenModalState.close();
    searchUnitModalState.open();
  };

  const handleDisconnect = () => {
    disconnectTrustpilotMutation();
  };

  const handleImport = (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  const handleBusinessUnitsUpdated = (businessUnits: TrustpilotBusinessUnit[]) => {
    setTrustpilotBusinessUnits(businessUnits);
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!trustpilotWorkspace}
        source={SOURCE_TYPE.TRUSTPILOT}
        description="Import reviews as feedback into Syncly."
        connectedDisplay={'Trustpilot'}
        info={trustpilotWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => tokenModalState.open()}
        additionalActions={(workspace) =>
          workspace.import_state === IMPORT_STATE.IMPORTING ? (
            <div className="flex items-center gap-2">
              <Spinner />
              <Typo type="body2" className="text-primary">
                Importing
              </Typo>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outlined"
                onClick={() => importModalState.open({ modalState: { isNew: false } })}
              >
                One-time import
              </Button>
              <Button
                variant="outlined"
                onClick={() => searchUnitModalState.open()}
              >
                Manage units
              </Button>
            </div>
          )
        }
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import Reviews'}
          subTitle={'Trustpilot'}
          historyList={trustpilotWorkspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImport({ start_date, end_date })
          }
        />
      </Modal>
      <Modal
        status={tokenModalState.status}
        closeModal={tokenModalState.close}
        useBackgroundClose
      >
        <TrustpilotTokenModal onConnectComplete={handleConnectComplete} />
      </Modal>
      {!!trustpilotWorkspace && (
        <Modal
          status={searchUnitModalState.status}
          closeModal={searchUnitModalState.close}
          useBackgroundClose
        >
          <TrustpilotSearchModal
            workspace={trustpilotWorkspace}
            onBusinessUnitsUpdated={handleBusinessUnitsUpdated}
          />
        </Modal>
      )}
    </>
  );
};
